import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PricingTier } from '@/lib/graphql/types-and-hooks';

/**
 * One line of a plan card's feature or limitation list.
 *
 * `key` is a CATALOG KEY, never display text: the copy lives in
 * `locales/<lng>/planFeatures.json` and is resolved at render time by
 * `useLocalizedPlans`. Two plans that advertise the same thing (Free and Tall
 * both list Pipeline Intelligence) share one key rather than repeating a string.
 */
export type PlanFeature = {
  key: string;
};

export type PlanDef = {
  tier: PricingTier;
  /** Product name — never translated, and `inheritFrom` matches against it. */
  name: string;
  taglineKey: string;
  monthlyPrice: number;
  annualPrice: number;
  monthlyPriceId: string;
  annualPriceId: string;
  perPerson: boolean;
  features: PlanFeature[];
  /** Notable things this plan does NOT include (rendered as a muted list). */
  limitations?: PlanFeature[];
  inheritFrom?: string;
  popular?: boolean;
  baseCredits: number;
};

/** Credit add-on packs — one-time purchases. Must match backend credit_packs.py. */
export const CREDIT_PACKS = [
  { id: 'pack_2000', credits: 2_000, price: 50, perCredit: 0.0250 },
  { id: 'pack_5000', credits: 5_000, price: 100, perCredit: 0.0200 },
  { id: 'pack_15000', credits: 15_000, price: 250, perCredit: 0.0167 },
  { id: 'pack_35000', credits: 35_000, price: 500, perCredit: 0.0143, popular: true },
  { id: 'pack_75000', credits: 75_000, price: 1000, perCredit: 0.0133 },
  { id: 'pack_250000', credits: 250_000, price: 2500, perCredit: 0.0100 },
] as const;

export type CreditPack = typeof CREDIT_PACKS[number];

export const PLANS: PlanDef[] = [
  {
    tier: PricingTier.Free,
    name: 'Free',
    taglineKey: 'planFeatures.tagline.free',
    monthlyPrice: 0,
    annualPrice: 0,
    // Free is $0/mo with no separate annual price; reuse the $0 monthly price
    // for both billing-cycle toggle states so checkout never hits an empty id.
    monthlyPriceId: import.meta.env.VITE_STRIPE_PRICE_FREE_MONTHLY || '',
    annualPriceId: import.meta.env.VITE_STRIPE_PRICE_FREE_MONTHLY || '',
    perPerson: false,
    baseCredits: 500,
    features: [
      { key: 'planFeatures.feature.upTo5Seats' },
      { key: 'planFeatures.feature.meetingsNotesAskCoffee' },
      { key: 'planFeatures.feature.pipelineIntelligence' },
      { key: 'planFeatures.feature.credits500' },
    ],
    limitations: [
      { key: 'planFeatures.limitation.meetingHistory90' },
      { key: 'planFeatures.limitation.records25k' },
      { key: 'planFeatures.limitation.noAppIntegrations' },
      { key: 'planFeatures.limitation.noApiOrMcp' },
    ],
  },
  {
    tier: PricingTier.Tall,
    name: 'Tall',
    taglineKey: 'planFeatures.tagline.tall',
    monthlyPrice: 25,
    annualPrice: 20,
    monthlyPriceId: import.meta.env.VITE_STRIPE_PRICE_TALL_MONTHLY || '',
    annualPriceId: import.meta.env.VITE_STRIPE_PRICE_TALL_ANNUAL || '',
    perPerson: true,
    popular: true,
    baseCredits: 1000,
    features: [
      { key: 'planFeatures.feature.noSeatLimits' },
      { key: 'planFeatures.feature.pipelineIntelligence' },
      { key: 'planFeatures.feature.slackNotifications' },
      { key: 'planFeatures.feature.appStore10' },
    ],
    limitations: [
      { key: 'planFeatures.limitation.noCrmIntegrations' },
      { key: 'planFeatures.limitation.noApiOrMcp' },
      { key: 'planFeatures.limitation.records100k' },
    ],
  },
  {
    tier: PricingTier.Grande,
    name: 'Grande',
    taglineKey: 'planFeatures.tagline.grande',
    monthlyPrice: 50,
    annualPrice: 40,
    monthlyPriceId: import.meta.env.VITE_STRIPE_PRICE_GRANDE_MONTHLY || '',
    annualPriceId: import.meta.env.VITE_STRIPE_PRICE_GRANDE_ANNUAL || '',
    perPerson: true,
    inheritFrom: 'Tall',
    baseCredits: 2000,
    features: [
      { key: 'planFeatures.feature.crmSync' },
      { key: 'planFeatures.feature.connectAiAssistants' },
      { key: 'planFeatures.feature.apiAccess' },
      { key: 'planFeatures.feature.appStoreUnlimited' },
    ],
  },
];

/** A feature line with its copy resolved into the reader's language. */
export type LocalizedPlanFeature = {
  text: string;
};

/** A plan card's data with `tagline`, `features` and `limitations` resolved. */
export type LocalizedPlan =
  Omit<PlanDef, 'taglineKey' | 'features' | 'limitations'> & {
    tagline: string;
    features: LocalizedPlanFeature[];
    limitations?: LocalizedPlanFeature[];
  };

/**
 * Plan cards with their copy in the reader's language.
 *
 * The resolution has to happen here rather than on `PLANS` itself: a `t()` call
 * at module scope would capture whatever language the app started in and never
 * update. Consumers keep reading `plan.tagline` and `feature.text`, so the only
 * thing a card has to change is where its array comes from.
 *
 * Prices, `tier`, `name`, `inheritFrom` and the Stripe price ids are wire and
 * identifier values, and are carried through untouched.
 */
export function useLocalizedPlans(plans: PlanDef[]): LocalizedPlan[] {
  const { t } = useTranslation();
  return useMemo(
    () =>
      plans.map(({ taglineKey, features, limitations, ...rest }) => ({
        ...rest,
        tagline: t(taglineKey),
        features: features.map(f => ({ text: t(f.key) })),
        ...(limitations ? { limitations: limitations.map(l => ({ text: t(l.key) })) } : {}),
      })),
    [plans, t],
  );
}
